import axios from 'axios';
import mockAnimals from '../data/mockAnimals';

// 使用Animality API获取随机动物数据
const ANIMAL_API_URL = 'https://api.animality.xyz/random';
// 使用Dog API获取随机狗图片
const DOG_API_URL = 'https://dog.ceo/api/breeds/image/random';
// 随机鸟API
const BIRD_API_URL = 'https://some-random-api.ml/animal/bird';
// 随机考拉API (替换原来的熊猫API)
const KOALA_API_URL = 'https://some-random-api.ml/animal/koala';

/**
 * 获取随机动物数据并适配为应用程序使用的格式
 * 
 * @returns {Promise<Object>} 包含动物数据的Promise对象
 */
export const getRandomAnimal = async () => {
  try {
    // 调用外部API获取随机动物数据
    const response = await axios.get(ANIMAL_API_URL);
    
    // 将API返回的数据格式转换为我们应用中使用的格式
    return {
      id: response.data.image_id,
      name: response.data.animal,
      image: response.data.image,
      description: response.data.fact,
      // 由于API没有提供以下字段，我们设置为null或默认值
      habitat: null,
      diet: null,
      lifespan: null
    };

  } catch (error) {
    console.error('获取随机动物数据失败:', error);
    
    // 如果API调用失败，使用本地模拟数据作为备选
    console.log('使用本地模拟数据作为备选...');
    const randomIndex = Math.floor(Math.random() * mockAnimals.length);
    return mockAnimals[randomIndex];
  }
};

/**
 * 获取随机狗图片并适配为应用程序使用的格式
 * 
 * @returns {Promise<Object>} 包含狗数据的Promise对象
 */
export const getRandomDog = async () => {
  try {
    // 调用Dog API获取随机狗图片
    const response = await axios.get(DOG_API_URL);
    
    // 从URL中提取品种信息
    let breed = "Unknown Breed";
    try {
      // URL格式通常是：https://images.dog.ceo/breeds/[breed]/[image].jpg
      const urlParts = response.data.message.split('/');
      if (urlParts.length > 4) {
        // 获取breed部分并格式化
        breed = urlParts[4].split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      }
    } catch (e) {
      console.log('无法解析狗品种:', e);
    }
    
    // 将API返回的数据格式转换为我们应用中使用的格式
    return {
      id: `dog-${Date.now()}`,  // 生成唯一ID
      name: `Dog (${breed})`,
      image: response.data.message,
      description: `这是一只可爱的${breed}。狗是人类最忠实的朋友，被认为是最早被人类驯化的动物之一。`,
      habitat: "家庭、农场和人类聚居地",
      diet: "肉食性，但已适应多种食物",
      lifespan: "10-13年（视品种而定）"
    };
  } catch (error) {
    console.error('获取随机狗图片失败:', error);
    throw error;
  }
};

/**
 * 获取随机鸟类图片和信息
 * 
 * @returns {Promise<Object>} 包含鸟类数据的Promise对象
 */
export const getRandomBird = async () => {
  try {
    // 调用Bird API获取随机鸟类信息
    const response = await axios.get(BIRD_API_URL);
    
    console.log('鸟类API返回数据:', response.data); // 添加日志便于调试
    
    // 将API返回的数据格式转换为我们应用中使用的格式
    return {
      id: `bird-${Date.now()}`,  // 生成唯一ID
      name: "Bird",
      image: response.data.image,
      fact: response.data.fact || "鸟类是具有羽毛、喙和产卵特征的温血脊椎动物。"
    };
  } catch (error) {
    console.error('获取随机鸟类数据失败:', error);
    throw error;
  }
};

/**
 * 获取随机考拉图片和信息
 * 
 * @returns {Promise<Object>} 包含考拉数据的Promise对象
 */
export const getRandomKoala = async () => {
  try {
    // 调用Koala API获取随机考拉信息
    const response = await axios.get(KOALA_API_URL);
    
    console.log('考拉API返回数据:', response.data); // 添加日志便于调试
    
    // 将API返回的数据格式转换为我们应用中使用的格式
    return {
      id: `koala-${Date.now()}`,  // 生成唯一ID
      name: "Koala",
      image: response.data.image,
      fact: response.data.fact || "考拉是澳大利亚特有的有袋类动物，以桉树叶为食。"
    };
  } catch (error) {
    console.error('获取随机考拉数据失败:', error);
    throw error;
  }
};

/**
 * 通过ID获取特定动物数据
 * 
 * @param {string} id 动物ID
 * @returns {Promise<Object>} 包含动物数据的Promise对象
 */
export const getAnimalById = async (id) => {
  try {
    // 由于当前API不支持通过ID获取数据，使用本地模拟数据
    const animal = mockAnimals.find(animal => animal.id === id);
    if (!animal) {
      throw new Error(`未找到ID为${id}的动物`);
    }
    return animal;
  } catch (error) {
    console.error(`获取ID为${id}的动物数据失败:`, error);
    throw error;
  }
};

/**
 * API服务对象
 */
const animalApi = {
  getRandomAnimal,
  getRandomDog,
  getRandomBird,
  getRandomKoala,
  getAnimalById
};

export default animalApi; 